import { hashKey, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  BulkEditListingClient,
  Event,
  Feature,
  ListingPriceCalculationsInEventInput,
} from 'src/WebApiController';

import { useDebouncedValueSafe } from '../useDebouncedValueSafe';
import { useUserHasFeature } from '../useUserHasFeature';

export const useListingGroupAutoPricePreview = (
  event: Event,
  listingIds: number[],
  rawPricingSettingsUpdates: ListingPriceCalculationsInEventInput | undefined
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const [debouncedPricingSettingsUpdate] = useDebouncedValueSafe(
    rawPricingSettingsUpdates
  );

  const shouldQuery = Boolean(
    activeAccountWebClientConfig.activeAccountId &&
      event.viagId &&
      !isEmpty(listingIds) &&
      debouncedPricingSettingsUpdate &&
      hasAutoPricingFeature
  );

  const autoPricingSettingsUpdates = useMemo(():
    | ListingPriceCalculationsInEventInput
    | undefined => {
    if (!debouncedPricingSettingsUpdate) {
      return undefined;
    }
    const { rankPremium, ...rest } = debouncedPricingSettingsUpdate;
    // Temp solution to get pricing calculation with most updated rank premium,
    // Pricer uses the cached group pricing settings
    // Override the hasChanged to be true to passdown whatever from UI
    return {
      ...rest,
      rankPremium: !rankPremium
        ? null
        : { hasChanged: true, value: rankPremium.value },
    };
  }, [debouncedPricingSettingsUpdate]);

  const listingCalculationsQuery = useQuery({
    queryKey: [
      'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
      activeAccountWebClientConfig.activeAccountId,
      event.viagId,
      hashKey([listingIds, autoPricingSettingsUpdates]),
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      const pricingSettings = await new BulkEditListingClient(
        activeAccountWebClientConfig
      ).bulkGetListingPriceCalculationsInEvent(event.viagId!, {
        item1: listingIds,
        item2: autoPricingSettingsUpdates!,
      });

      return pricingSettings;
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    // The response of the price calculation is huge
    // We don't want to cache the data and eat up the heap mem as the query is per input
    gcTime: 0,
    meta: {
      onError: (error: ErrorTypes) => {
        showErrorDialog(
          'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
          error,
          {
            trackErrorData: {
              activeAccountId: activeAccountWebClientConfig.activeAccountId,
              viagogoEventId: event.viagId,
              listingIds,
              autoPricingSettingsUpdates,
            },
          }
        );
      },
    },
  });

  return {
    listingCalculationsQuery,
    bulkListingPriceCalculationResult: listingCalculationsQuery.data,
    isLoading: listingCalculationsQuery.isLoading,
  };
};
